/**
 * @summary - Component that Provides Standard Task Form
 * @author Purushothaman T
 * @since 19-06-2024
 */
import { Grid } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import {
  standardtask,
  validations,
} from "../../utility/constants/errors/messages";
import { BemsGraph, FemsGraph, MasterGraph } from "../../utility/apiURLS";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
  getuserId,
} from "../../utility/tokenUtility";
import utils from "../../utility/utilities";
import ActionButtons from "../commoncomponents/ActionButtons";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import CustomDialog from "../commoncomponents/CustomDialog";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomTextArea from "../commoncomponents/CustomTextArea";
import CustomTextField from "../commoncomponents/CustomTextField";
import PageTitle from "../commoncomponents/PageTitle";
import { dialogContent } from "../../utility/message";

const StandardTaskForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthToken("id_token");
  const facilityId = getFacilityId();
  const userId = getuserId();
  const companyId = getcompanyId();
  const [isChanged, setIsChanged] = useState(false);
  const currentDate = utils.getCurrentDateFormatted("DD/MM/YYYY");
  const active = {
    label: "Active",
    value: "Active",
    id: true,
  };
  const inActive = {
      label: "Inactive",
      value: "Inactive",
      id: false,
    },
    statusOptions = [active, inActive];
  useEffect(() => {
    if (location.state) {
      const { taskData, isEditMode, isViewMode } = location.state;
      setTaskData(taskData);
      setIsEdit(isEditMode);
      setIsView(isViewMode);
    }
  }, [location.state]);
  const [taskData, setTaskData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [taskcode, setTaskcode] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [status, setStatus] = useState(statusOptions[0]);
  const [effectivefrom, setEffectivefrom] = useState("");
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [workgroup, setWorkgroup] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [contentDialog, setContentDialog] = useState("");
  const [workGroupOptions, setWorkGroupOptions] = useState([]);
  const [responseWorkgroupArray, setResponseWorkgroupArray] = useState([]);
  const [enableSubmit, setEnableSubmit] = useState(true);
  const serviceRef = useRef(null);
  const descRef = useRef(null);
  const [isAddNew, setIsAddNew] = useState(false);
  const [minDate, setMindate] = useState("");
  const [errors, setErrors] = useState({
    service: false,
    workgroup: false,
    taskcode: false,
    taskdesc: false,
    effectivefrom: false,
    workinstructions: false,
  });
  const [errorMessages, setErrorMessages] = useState({
    service: "",
    workgroup: "",
    taskcode: "",
    taskdesc: "",
    effectivefrom: "",
    workinstructions: "",
  });

  useEffect(() => {
    if (taskData.femtaskInUse || taskData.bemtaskInUse) {
      descRef.current.focus();
    } else {
      serviceRef.current.focus();
    }
  }, [taskData]);

  useEffect(() => {
    fetchEditData();
    fetchOptions();
  }, [isEdit, isView]);

  const fetchOptions = async () => {
    try {
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };
      const serviceResponse = await axios.get(
        `${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`,
        config
      );
      const workgroupResponseBems = await axios.get(
        `${BemsGraph}/api/GraphWorkGroupDetails/GetWorkGroupDetailsByFacilityId/${facilityId}?size=100`,
        config
      );
      const workgroupResponseFems = await axios.get(
        `${FemsGraph}/api/GraphWorkGroupDetails/GetWorkGroupDetailsByFacilityId/${facilityId}?size=100`,
        config
      );
      const serviceData = serviceResponse.data.masservicedetails;
      setResponseWorkgroupArray([
        ...workgroupResponseBems.data.map((data) => {
          return {
            id: data.id,
            value: `${data.wgcode} - ${data.wgdesc}`,
            label: `${data.wgcode} - ${data.wgdesc}`,
            serviceId: data.serviceid,
          };
        }),
        ...workgroupResponseFems.data.map((data) => {
          return {
            id: data.id,
            value: `${data.wgcode} - ${data.wgdesc}`,
            label: `${data.wgcode} - ${data.wgdesc}`,
            serviceId: data.serviceid,
          };
        }),
      ]);

      setServiceOptions(
        serviceData.map((service) => ({
          value: service.service,
          label: service.service,
          id: service.id,
        }))
      );
    } catch (error) {
      console.error("Error fetching service options:", error);
    }
  };

  useEffect(() => {
    setWorkGroupOptions(
      responseWorkgroupArray.filter((data) => data.serviceId === service.id)
    );
  }, [service, responseWorkgroupArray]);

  const fetchEditData = () => {
    setService(isEdit || isView ? taskData.service : "");
    setWorkgroup(isEdit || isView ? taskData.workgroup : "");
    setTaskcode(isEdit || isView ? taskData.taskcode : "");
    setDescription(isEdit || isView ? taskData.taskdesc : "");
    setInstructions(isEdit || isView ? taskData.workinstructions : "");
    if (isEdit || isView) {
      setStatus(taskData.isactive == "Active" ? active : inActive);
      setEffectivefrom(taskData.effectivefrom);
      setMindate(taskData.effectivefrom);
    } else {
      setStatus(statusOptions[0]);
      setEffectivefrom(currentDate);
    }
  };

  const handleCancel = () => {
    if (!isChanged) {
      navigate("/standardtask");
    }

    setOpenDialog(true);
    setContentDialog(dialogContent.modified);
  };

  const handleAddNew = () => {
    if (isChanged) {
      setIsAddNew(true);
      setOpenDialog(true);
      setContentDialog(dialogContent.modified);
    } else {
      setDeafault();
    }
  };

  const handleDialogClose = () => {
    if (isChanged) {
      setOpenDialog(false);
    }
    if (isAddNew) {
      setIsAddNew(false);
    }
  };

  const handleDialogConfirm = () => {
    setOpenValidationDialog(false);
    setOpenDialog(false);
    if (isChanged && !openValidationDialog && !isAddNew) {
      navigate("/standardtask");
    }
    if (isAddNew) {
      setDeafault();
    }
  };
  const setDeafault = () => {
    setEnableSubmit(true);
    setService("");
    setWorkgroup("");
    setTaskcode("");
    setStatus(statusOptions[0]);
    setDescription("");
    setInstructions("");
    setErrors({});
    setErrorMessages({});
    setEffectivefrom(currentDate);
    setIsAddNew(false);
    setIsChanged(false);
  };
  const handleStatusChange = (newValue) => {
    if (taskData?.isactive == "Active") {
      if (newValue.value == "Inactive") {
        setEffectivefrom(currentDate);
        setMindate(currentDate);
      }
      if (newValue.value == "Active") {
        if (isEdit) {
          setMindate(taskData.effectivefrom);
        } else {
          setMindate("");
        }
        setEffectivefrom(
          taskData?.effectivefrom ? taskData.effectivefrom : currentDate
        );
      }
    } else {
      if (newValue.value == "Active") {
        setEffectivefrom(currentDate);
        setMindate(currentDate);
      }
      if (newValue.value == "Inactive") {
        if (isEdit) {
          setMindate(taskData.effectivefrom);
        } else {
          setMindate("");
        }
        setEffectivefrom(
          taskData?.effectivefrom ? taskData.effectivefrom : currentDate
        );
      }
    }
    setIsChanged(true);
    setStatus(newValue);
  };

  const handleServiceChange = (newValue) => {
    setIsChanged(true);
    setWorkgroup(null);
    setService(newValue);
    if (!service) {
      setErrors((prev) => ({ ...prev, service: false }));
      setErrorMessages((prev) => ({ ...prev, service: "" }));
    }
  };

  const handleWorkgroupChange = (newValue) => {
    setIsChanged(true);
    setWorkgroup(newValue);
    if (newValue) {
      setErrors((prev) => ({ ...prev, workgroup: false }));
      setErrorMessages((prev) => ({ ...prev, workgroup: "" }));
    }
  };

  const handletaskcodeChange = (newValue) => {
    setIsChanged(true);
    setTaskcode(newValue);
    if (newValue) {
      setErrors((prev) => ({ ...prev, taskcode: false }));
      setErrorMessages((prev) => ({ ...prev, taskcode: "" }));
    }
    if (utils.validateTextFieldCode(newValue) || !newValue) {
      setErrors((prev) => ({ ...prev, taskcode: false }));
      setErrorMessages((prev) => ({ ...prev, taskcode: "" }));
    } else if (!utils.validateTextFieldCode(newValue)) {
      setErrors((prev) => ({ ...prev, taskcode: true }));
      setErrorMessages((prev) => ({
        ...prev,
        taskcode: validations.textfieldCode,
      }));
    }
  };
  const handleTaskDescChange = (newValue) => {
    setIsChanged(true);
    setDescription(newValue);
    if (newValue) {
      setErrors((prev) => ({ ...prev, taskdesc: false }));
      setErrorMessages((prev) => ({ ...prev, taskdesc: "" }));
    }
    if (utils.validateTextField(newValue) || !newValue) {
      setErrors((prev) => ({ ...prev, taskdesc: false }));
      setErrorMessages((prev) => ({ ...prev, taskdesc: "" }));
    } else if (!utils.validateTextField(newValue)) {
      setErrors((prev) => ({ ...prev, taskdesc: true }));
      setErrorMessages((prev) => ({
        ...prev,
        taskdesc: validations.textfield,
      }));
    }
  };

  const handleeffectivefromChange = (newValue) => {
    setIsChanged(true);
    setEffectivefrom(newValue);
    setIsChanged(true);
    if (newValue) {
      setErrors((prev) => ({ ...prev, effectivefrom: false }));
      setErrorMessages((prev) => ({ ...prev, effectivefrom: "" }));
    }
  };

  const handleWorkinstructionsChange = (newValue) => {
    setIsChanged(true);
    setInstructions(newValue);
    if (utils.validateTextField(newValue) || !newValue) {
      setErrors((prev) => ({ ...prev, workinstructions: false }));
      setErrorMessages((prev) => ({ ...prev, workinstructions: "" }));
    } else if (!utils.validateTextField(newValue)) {
      setErrors((prev) => ({ ...prev, workinstructions: true }));
      setErrorMessages((prev) => ({
        ...prev,
        workinstructions: validations.textfield,
      }));
    }
  };

  const handleSubmit = (e) => {
    let isFormValid = true;
    if (!service) {
      isFormValid = false;
      setErrors((prev) => ({ ...prev, service: true }));
      setErrorMessages((prev) => ({
        ...prev,
        service: standardtask.service.required,
      }));
    }
    if (!workgroup) {
      isFormValid = false;
      setErrors((prev) => ({ ...prev, workgroup: true }));
      setErrorMessages((prev) => ({
        ...prev,
        workgroup: standardtask.workgroup.required,
      }));
    }
    if (!taskcode) {
      isFormValid = false;
      setErrors((prev) => ({ ...prev, taskcode: true }));
      setErrorMessages((prev) => ({
        ...prev,
        taskcode: standardtask.taskcode.required,
      }));
    }
    if (!description) {
      isFormValid = false;
      setErrors((prev) => ({ ...prev, taskdesc: true }));
      setErrorMessages((prev) => ({
        ...prev,
        taskdesc: standardtask.taskdescription.required,
      }));
    }
    if (!effectivefrom) {
      isFormValid = false;
      setErrors((prev) => ({ ...prev, effectivefrom: true }));
      setErrorMessages((prev) => ({
        ...prev,
        effectivefrom: standardtask.effectivefrom.required,
      }));
    }
    if (
      errors.service ||
      errors.workgroup ||
      errors.taskcode ||
      errors.taskdesc ||
      errors.effectivefrom ||
      errors.workinstructions
    ) {
      isFormValid = false;
    }

    if (isFormValid) {
      handlesave();
    }
  };
  const handlesave = async () => {
    const postData = {
      id: isEdit ? taskData.id : "",
      facilityid: facilityId,
      taskcode: taskcode?.trimEnd(),
      taskdesc: description?.trimEnd(),
      workinstructions: instructions?.trimEnd(),
      effectivefrom: utils.formatDate({
        dateString: effectivefrom,
        format: "YYYY-MM-DD",
        includeTime: true,
      }),
      isactive: status.id,
      modifiedby: userId,
      timestamp: isEdit ? taskData.timestamp : "",
      isdeleted:taskData.isdeleted
    };

    const token = getAuthToken("id_token");
    const config = {
      headers: {
        // Attach bearer token to request header
        Authorization: `Bearer ${token}`,
      },
    };
    let endpoint;
    if (service.value === "FEMS") {
      endpoint = isEdit
        ? `${FemsGraph}/api/femtaskdetails/${taskData.id}`
        : `${FemsGraph}/api/femtaskdetails`;
      postData["femworkgroupdetailsId"] =  workgroup.id
    } else if (service.value === "BEMS") {
      endpoint = isEdit
        ? `${BemsGraph}/api/bemtaskdetails/${taskData.id}`
        : `${BemsGraph}/api/bemtaskdetails`;
      postData["bemworkgroupdetailsId"] =  workgroup.id
    }
    setErrors((prev) => ({ ...prev, taskcode: false }));
    setErrorMessages((prev) => ({
      ...prev,
      taskcode: "",
    }));
    if (!isEdit) {
      await postMethod(endpoint, postData, config);
    }
    if (isEdit) {
      if (service === taskData.service) {
        const res = await putMethod(endpoint, postData, config);
      } else {
        postData["id"] = "";
        const endpointPost =
          service.value === "FEMS"
            ? `${FemsGraph}/api/femtaskdetails`
            : `${BemsGraph}/api/bemtaskdetails`;

        const endpointDelete =
          service.value === "FEMS"
            ? `${BemsGraph}/api/bemtaskdetails/${taskData.id}`
            : `${FemsGraph}/api/femtaskdetails/${taskData.id}`;

        const postResponse = await postMethod(
          endpointPost,
          postData,
          config,
          true
        );
        if (postResponse.status == 201) {
          await deleteMethod(endpointDelete, config);
          setTaskData((prev) => ({
            ...prev,
            service: service,
            id: postResponse.data.id,
          }));
        }
      }
    }
  };

  const postMethod = async (endpoint, postData, config, isUpdate = false) => {
    try {
      let result = await axios({
        method: "post",
        url: endpoint,
        data: postData,
        headers: config.headers,
      });
      if (result.status === 201) {
        setContentDialog(
          isUpdate ? dialogContent.updated : dialogContent.saved
        );
        setIsChanged(false);
        setOpenValidationDialog(true);
        if (!isUpdate) {
          setEnableSubmit(false);
        }
      }
      return result;
    } catch (error) {
      if (error.response.status === 400) {
        setOpenValidationDialog(true);
        setContentDialog(error.response.data.detail || validations.default);
      }
      console.log(error);
    }
  };

  const putMethod = async (endpoint, postData, config) => {
    try {
      await axios({
        method: "put",
        url: endpoint,
        data: postData,
        headers: config.headers,
      }).then((response) => {
        if (response.status === 200) {
          setTaskData((prev) => ({
            ...prev,
            isactive: status.label,
            effectivefrom: effectivefrom,
          }));
          setContentDialog(dialogContent.updated);
          setIsChanged(false);
          setOpenValidationDialog(true);
        }
      });
    } catch (error) {
      if (error.response.status === 400) {
        setContentDialog(error ? error.response.data.detail : "Error Occured!");
        setOpenValidationDialog(true);
      }
    }
  };

  const deleteMethod = async (endpoint, config) => {
    try {
      let result = await axios({
        method: "delete",
        url: endpoint,
        headers: config.headers,
      });
      return result;
    } catch (error) {
      if (error.response.status === 400) {
        setContentDialog(error ? error.response.data.detail : "Error Occured!");
        setOpenValidationDialog(true);
      }
      return error;
    }
  };

  return (
    <>
      <PageTitle
        isEdit={isEdit}
        isView={isView}
        editTitle={"Edit Task"}
        viewTitle={"View Task"}
        title={"Create New Task"}
      />
      <DefaultCard>
        <Grid container spacing={4}>
          <Grid item xs={6} md={4}>
            <CustomSelectInput
              error={errors.service}
              disableClearable={true}
              label="Service"
              isMandatoryField={true}
              value={service}
              onChange={handleServiceChange}
              options={serviceOptions}
              disabled={
                taskData
                  ? isView || taskData.femtaskInUse || taskData.bemtaskInUse
                  : false
              }
              id="service"
              helperText={errorMessages.service}
              inputRef={serviceRef}
            />
          </Grid>

          <Grid item xs={6} md={4}>
            <CustomSelectInput
              error={errors.workgroup}
              disableClearable={true}
              label="Work Group"
              isMandatoryField={true}
              options={workGroupOptions}
              getOptionLabel={(option) => option.label}
              value={workgroup}
              onChange={handleWorkgroupChange}
              id="workgroup"
              helperText={errorMessages.workgroup}
              disabled={
                taskData
                  ? isView || taskData.femtaskInUse || taskData.bemtaskInUse
                  : false
              }
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <CustomTextField
              label="Task Code"
              isMandatoryField={true}
              id="taskcode"
              value={taskcode}
              maxlength={10}
              onChange={handletaskcodeChange}
              disabled={isEdit || isView ? true : false}
              error={errors.taskcode}
              helperText={errorMessages.taskcode}
            />
          </Grid>

          <Grid item xs={6} md={4}>
            <CustomTextField
              label="Task Description"
              isMandatoryField={true}
              id="task-description"
              disabled={isView ? true : false}
              value={description}
              maxlength={255}
              onChange={handleTaskDescChange}
              error={errors.taskdesc}
              helperText={errorMessages.taskdesc}
              inputRef={descRef}
            />
          </Grid>

          <Grid item xs={6} md={4}>
            <CustomSelectInput
              value={status}
              options={statusOptions}
              onChange={handleStatusChange}
              disableClearable={true}
              disabled={isView || !isEdit ? true : false}
              label={"Status"}
              defaultOption={statusOptions[0]}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <CustomDatePicker
              maxDate={currentDate}
              minimumDate={minDate}
              label="Effective From"
              isMandatoryField={true}
              id="effective-from"
              value={effectivefrom}
              disabled={isView || minDate === currentDate || !isEdit}
              onDateChange={handleeffectivefromChange}
              error={errors.effectivefrom}
              helperText={errorMessages.effectivefrom}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <CustomTextArea
              label="Work Instructions"
              id="modal-task-instructions"
              value={instructions}
              disabled={isView ? true : false}
              maxLength={500}
              onChange={handleWorkinstructionsChange}
              error={errors.workinstructions}
              helperText={errorMessages.workinstructions}
            />
          </Grid>
        </Grid>

        <ActionButtons
          handleCancel={handleCancel}
          handleSubmit={(e) => handleSubmit(e)}
          handleAddNew={handleAddNew}
          enableSubmit={enableSubmit}
          isEdit={isEdit}
          isView={isView}
        />
      </DefaultCard>
      <CustomDialog
        cancelBtn={"No"}
        open={openDialog}
        onConfirm={handleDialogConfirm}
        content={contentDialog}
        onClose={handleDialogClose}
        confirmBtn={"Yes"}
      />
      <CustomDialog
        open={openValidationDialog}
        onConfirm={handleDialogConfirm}
        content={contentDialog}
        onClose={handleDialogClose}
        confirmBtn={"Ok"}
      />
    </>
  );
};

export default StandardTaskForm;
